import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AlertsAndInstructions from "src/features/common/alertsAndInstructions";
import CardWidget from "src/app/components/card";
import {
  CellTowerOutlined,
  SignpostOutlined,
  TrendingUp,
  DescriptionOutlined,
  VisibilityOutlined,
  SyncDisabled,
  WarningAmber,
} from "@mui/icons-material";
import GlobalIndicatorChart from "../../globalIndicator";
import ReportsCard from "src/features/common/reports";
import LiveStatus from "./live-status";
import Signal from "./signal";
import Trends from "./trends";
import CylinderIndicator from "./cylinder-indicator";
import PeakPressure from "./peak-pressure";
import { SIGNAL_STATUS_QUALITY } from "../../schema";
import axios from "axios";
import {
  buildLiveStatusData,
  convertUTCDateToLocalTime,
} from "src/app/utils/helper";
import DownloadPngModal from "src/features/configuration/modals/downloadPngModal";
import { toPng } from "html-to-image";
import useWebSocket from "react-use-websocket";
import api from "src/app/api";
import { USER_NAME_ROLE } from "src/app/auth";

const normalizeSettingValue = (value: any) =>
  value === true || value === "true" || value === "1" || value === 1;

const isTwistAngleAlert = (alert: any) => {
  const name = (alert?.instructionName || "").toString().toLowerCase();
  return name === "twist angle" || name.includes("twist");
};

const buildLiveTwistAngleAlert = (twistIndicator: any, rpmIndicator: any) => {
  const rawTwistValue = parseFloat(twistIndicator?.indicatorValue);
  if (Number.isNaN(rawTwistValue) || rawTwistValue <= 0.4) {
    return null;
  }

  const rpmLabel =
    rpmIndicator?.indicatorValue !== undefined &&
    rpmIndicator?.indicatorValue !== null &&
    rpmIndicator?.indicatorValue !== ""
      ? rpmIndicator.indicatorValue
      : "X";

  return {
    instructionName: `High Twist Angle at "${rpmLabel}" RPM. Consider Avoiding continuous operation at this Speed`,
    instructionType: "error",
    instructions: [],
  };
};

const getCylinderIndicatorsSignature = (indicators: any[] = []) => {
  return indicators
    .map((item: any) => {
      const root = item?.graphData?.[0] || {};
      const childSig = (root?.children || [])
        .map((child: any) => {
          const nestedSig = (child?.children || [])
            .map(
              (nested: any) =>
                `${nested?.name || ""}:${nested?.showValue || ""}:${nested?.fill || ""}`
            )
            .join("|");
          return `${child?.name || ""}:${child?.showValue || ""}:${child?.fill || ""}:${nestedSig}`;
        })
        .join(";");

      return `${item?.graphLabel || ""}:${root?.showValue || ""}:${root?.fill || ""}:${childSig}`;
    })
    .join("||");
};

const ModuleMonitoringPage = ({
  moduleId,
  configurationId,
  moduleData,
  classes,
  trendsData,
  processName,
  formData,
  moduleType,
  status,
}: any) => {
  const [twistAngleEnabled, setTwistAngleEnabled] = useState(
    typeof window !== "undefined" && window.sessionStorage
      ? normalizeSettingValue(window.sessionStorage.getItem("twist_angle_page"))
      : false
  );

  // Monitor sessionStorage for twist angle changes
  useEffect(() => {
    const interval = setInterval(() => {
      const currentSetting =
        typeof window !== "undefined" && window.sessionStorage
          ? normalizeSettingValue(window.sessionStorage.getItem("twist_angle_page"))
          : false;
      setTwistAngleEnabled(currentSetting);
    }, 500); // Check every 500ms

    return () => clearInterval(interval);
  }, []);

  const baseAlertData = Array.isArray(trendsData?.alert) ? trendsData.alert : [];
  const alertDataWithoutTwist = baseAlertData.filter(
    (item: any) => !isTwistAngleAlert(item)
  );

  const liveTwistIndicator = (moduleData?.globalIndicator || []).find(
    (item: any) => item?.indicatorName === "Twist Angle"
  );
  const liveRpmIndicator = (moduleData?.globalIndicator || []).find(
    (item: any) => item?.indicatorName === "RPM"
  );

  const liveTwistAngleAlert =
    moduleType === "Engine" && twistAngleEnabled
      ? buildLiveTwistAngleAlert(liveTwistIndicator, liveRpmIndicator)
      : null;

  const fallbackDashboardAlertData = liveTwistAngleAlert
    ? [...alertDataWithoutTwist, liveTwistAngleAlert]
    : alertDataWithoutTwist;

  const [alertsApiData, setAlertsApiData] = useState<any[]>([]);

  const mapAlertRowsToUiData = (rows: any[] = []) => {
    return rows.map((row: any) => {
      let parsed: any = row?.alert_msg_parsed;
      if (!parsed && row?.alert_msg) {
        try {
          parsed = JSON.parse(row.alert_msg);
        } catch (e) {
          parsed = null;
        }
      }

      return {
        instructionName: parsed?.name || row?.indicator_name || "Alert",
        instructionType: row?.severity === "alert" ? "error" : "warning",
        instructions: Array.isArray(parsed?.instructions) ? parsed.instructions : [],
      };
    });
  };

  const fetchAlertsData = () => {
    if (!moduleId) {
      setAlertsApiData([]);
      return;
    }

    const params: any = {
      module_id: moduleId,
      active_configuration_only: "1",
      is_active: "1",
      limit: 500,
      user_role: sessionStorage.getItem(USER_NAME_ROLE) || "",
    };

    if (configurationId !== undefined && configurationId !== null && `${configurationId}`.trim() !== "") {
      params.configuration_id = configurationId;
    }

    api
      .get("/alerts/getAlerts.php", { params })
      .then((response: any) => {
        const payload = response?.data;
        if (payload?.Status === "Success" && Array.isArray(payload?.data)) {
          setAlertsApiData(mapAlertRowsToUiData(payload.data));
          return;
        }
        setAlertsApiData([]);
      })
      .catch(() => {
        setAlertsApiData([]);
      });
  };

  useEffect(() => {
    fetchAlertsData();
  }, [moduleId, configurationId, moduleType]);

  const dashboardAlertData =
    moduleType === "Torque" ? fallbackDashboardAlertData : alertsApiData;

  const [activeModule, setActiveModule] = useState<number>(0);
  const [signalData, setSignalData] = useState<any>({});
  const [isLiveSocket, setIsliveSocket] = useState<boolean>(false);
  const [isLatestReportOpen, setIsLatestReportOpen] = useState<boolean>(false);
  const [isLiveStatusOpen, setIsLiveStatusOpen] = useState<boolean>(false);
  const [trendsCylinder, setTrendsCylinder] = useState<string[]>([]);
  const [liveStatus, setLiveStatus] = useState<any>({});
  const [currentMode, setCurrentMode] = useState<any>("");
  const [documents, setDocuments] = useState<any>([]);
  const elementRef = useRef<(HTMLDivElement | null)[]>([]);
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showChip, setShowChip] = useState<boolean>(false);
  const [liveCylinderIndicators, setLiveCylinderIndicators] = useState<any[]>(
    []
  );

  const onActiveModuleChange = (event: any, params: any) => {
    setActiveModule(params);
  };

  const parsedFormData = JSON.parse(formData);

  const [staticBackward, setStaticBackward] = useState<number | null>(null);
  const [staticStatus, setStaticStatus] = useState<number | null>(null);
  const [fetchedStatus, setFetchedStatus] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { sendMessage, lastMessage, readyState } = useWebSocket(
    process.env.REACT_APP_LIVE_WEBSOCKET_URL ||
      `ws:${window.location.hostname}:8082`,
    {
      shouldReconnect: (closeEvent) => true,
    },
    isLiveSocket
  );

  // Poll interval: 1s during active steps (Recording/Analysing), 5s during Idle/Report
  const [pollInterval, setPollInterval] = useState(5000);

  useEffect(() => {
    const step = liveStatus?.currentStep;
    if (step === 2 || step === 3) {
      setPollInterval(1000); // Fast polling during recording/analysing
    } else {
      setPollInterval(5000); // Slow polling during idle/report
    }
  }, [liveStatus?.currentStep]);

  // Poll at dynamic interval for fresh data
  useEffect(() => {
    if (!isLiveSocket || readyState !== 1) return;
    // Send immediately on connect
    if (sendMessage) sendMessage(processName);
    const interval = setInterval(() => {
      if (sendMessage) sendMessage(processName);
    }, pollInterval);
    return () => clearInterval(interval);
  }, [isLiveSocket, readyState, sendMessage, processName, pollInterval]);

  useEffect(() => {
    if (lastMessage !== undefined) {
      const data = lastMessage?.data;
      if (data) {
        let parsedData = data;
        try {
          parsedData = JSON.parse(data);
        } catch (e) {
          // keep as is if already object
        }
        if (parsedData?.Status !== "Failed") {
          const transformed = buildLiveStatusData(parsedData);
          setLiveStatus((prev: any) => {
            // Prevent going backward from step 4 to step 3 (stuttering during transition)
            if (prev?.currentStep === 4 && transformed.currentStep === 3) {
              return prev;
            }
            // Preserve previous processingPercent at step 3 if new data is missing it
            if (transformed.currentStep === 3 && !transformed.processingPercent && prev?.processingPercent) {
              return { ...transformed, processingPercent: prev.processingPercent };
            }
            return transformed;
          });
        }
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}dashboard/trends.php/${moduleId}`
        );
        const data = response.data;

        if (data.Status === "Fail") {
          setError(data.Message);
        } else if (data.file_data) {
          const fileData = data.file_data;

          const latestTimestamp = Object.keys(fileData).sort().pop();
          if (latestTimestamp) {
            const staticBackwardValue =
              fileData[latestTimestamp].StaticBackward?.value;

            setStaticBackward(parseFloat(staticBackwardValue));

            const statusValue = fileData[latestTimestamp].Status;
            setFetchedStatus(parseFloat(statusValue));
            console.log(statusValue);
            const staticStatusValue =
              fileData[latestTimestamp].StaticStatus?.value;
            setStaticStatus(parseFloat(staticStatusValue));

            if (
              (staticBackwardValue == 1 &&
                statusValue == 4 &&
                staticStatusValue >= 0 &&
                staticStatusValue != 32) ||
              (staticBackwardValue == 0 &&
                statusValue == 4 &&
                staticStatusValue >= 0 &&
                staticStatusValue != 32) ||
              (staticBackwardValue == 1 &&
                statusValue == 4 &&
                staticStatusValue >= 0 &&
                staticStatusValue == 32) ||
              (staticBackwardValue == 0 &&
                statusValue == 4 &&
                staticStatusValue >= 0 &&
                staticStatusValue == 32)
            ) {
              setShowChip(true);
            } else {
              setShowChip(false);
            }
          } else {
            setError("No data available");
          }
        } else if (Object.keys(data).length === 0) {
          setError("Received empty data object");
        } else {
          setError("Unexpected data format");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data");
      }
    };

    fetchData();
  }, [moduleId]);

  useEffect(() => {
    const data = SIGNAL_STATUS_QUALITY.find(
      (val) => val.id == moduleData?.currentStatus
    );
    setSignalData(data || SIGNAL_STATUS_QUALITY[0]);
  }, [moduleData?.currentStatus]);

  useEffect(() => {
    if (moduleData?.cylinder_specific_indicators?.length > 0) {
      setLiveCylinderIndicators((prev: any[]) => {
        const next = moduleData.cylinder_specific_indicators;
        const prevSignature = getCylinderIndicatorsSignature(prev);
        const nextSignature = getCylinderIndicatorsSignature(next);
        if (prevSignature === nextSignature) {
          return prev;
        }
        return next;
      });
    }
  }, [moduleData?.cylinder_specific_indicators]);

  const cylinderIndicatorData =
    liveCylinderIndicators.length > 0
      ? liveCylinderIndicators
      : trendsData?.cylinder_specific_indicators || [];

  useEffect(() => {
    setIsliveSocket(isLatestReportOpen || isLiveStatusOpen);
  }, [isLatestReportOpen, isLiveStatusOpen]);

  useEffect(() => {
    if (isLiveSocket && sendMessage) {
      sendMessage(processName);
    }
  }, [isLiveSocket]);

  useEffect(() => {
    const trendsCylinderArr = ["Trends"];
    if (
      trendsData.cylinder_specific_indicators &&
      trendsData.cylinder_specific_indicators.length > 0
    ) {
      trendsCylinderArr.push("Cylinder Specific Indicator");
    }
    console.log(fetchedStatus);
    if (
      (fetchedStatus === 3 &&
        trendsData.pressureReadings &&
        trendsData.pressureReadings.length > 0) ||
      (trendsData.trends &&
        trendsData.trends[2] &&
        trendsData.trends[2].datapoints &&
        trendsData.trends[2].datapoints.length > 0 &&
        fetchedStatus === 3)
    ) {
      trendsCylinderArr.push("Peak Pressure"); // Add Peak Pressure tab if data is present
    }
    setTrendsCylinder(trendsCylinderArr);
  }, [trendsData, fetchedStatus]);

  useEffect(() => {
    const fileName = `${parsedFormData?.asset_name} - ${
      parsedFormData?.equipment_name
    } - ${convertUTCDateToLocalTime(new Date())}`;
    if (documents.length > 0) {
      setTimeout(function () {
        elementRef.current.map((val: any) => {
          if (val) {
            toPng(val, { quality: 0.5 })
              .then((dataUrl: string) => {
                const link = document.createElement("a");
                link.download = `${fileName}.png`;
                link.href = dataUrl;
                setIsLoading(false);
                link.click();
                setDocuments([]);
              })
              .catch((err) => {
                setIsLoading(false);
                setDocuments([]);
              });
          }
        });
      }, 1000);
    }
  }, [documents]);

  return (
    <Grid container spacing={1}>
      <Grid item xl={5} lg={3} md={12} sm={12}>
        <CardWidget
          showDate={trendsData?.alertUpdatedOn}
          headerLabel={
            moduleType !== "Torque"
              ? "Alerts & Instructions"
              : "Status Messages"
          }
          headerIcon={<WarningAmber />}
          content={
            <AlertsAndInstructions
              moduleData={moduleData}
              alertData={dashboardAlertData}
            />
          }
          formData={formData}
          moduleType={moduleType}
          moduleId={moduleId}
          initiallyCollapsed={false}
          onAlertRefresh={fetchAlertsData}
          fullScreenContent={
            <AlertsAndInstructions
              moduleData={moduleData}
              alertData={dashboardAlertData}
              isModalOpen={true}
            />
          }
          section="top"
        />
      </Grid>
      <Grid item xs={12} md={12} lg={9} xl={7}>
        <CardWidget
          showDate={trendsData.alertUpdatedOn}
          headerLabel="Global Indicators"
          headerIcon={<SignpostOutlined />}
          content={
            <Box>
              <GlobalIndicatorChart
                globalIndicator={moduleData?.globalIndicator || []}
                fullScreen={false}
                isTorqueModule={moduleType === "Torque"}
              />
              {moduleType === "Torque" && showChip && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Chip
                    label={(function () {
                      if (
                        fetchedStatus === 4 &&
                        staticStatus != null && // Null check
                        staticStatus >= 0 &&
                        staticStatus !== 32
                      ) {
                        return staticBackward === 1
                          ? "Direction Backward (Astern)"
                          : staticBackward === 0
                          ? "Direction Forward (Ahead)"
                          : "NA";
                      } else if (
                        (fetchedStatus === 4 &&
                          staticStatus != null && // Null check
                          staticStatus >= 0 &&
                          staticStatus === 32 &&
                          staticBackward === 1) ||
                        0
                      ) {
                        return "Undefined";
                      } else {
                        return "NA";
                      }
                    })()}
                    color="primary"
                    sx={{
                      color: "white", // Text color for contrast
                    }}
                  />
                </Box>
              )}
            </Box>
          }
          initiallyCollapsed={false}
          fullScreenContent={
            <GlobalIndicatorChart
              showDate={trendsData.alertUpdatedOn}
              globalIndicator={moduleData?.globalIndicator || []}
              fullScreen={true}
              isTorqueModule={moduleType === "Torque"}
            />
          }
          section="top"
        />
      </Grid>
      <Grid item xs={12}>
        <CardWidget
          headerContent={
            <>
              <Tabs
                value={activeModule}
                onChange={onActiveModuleChange}
                aria-label="select modules"
                sx={{ marginTop: "10px" }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {trendsCylinder?.map((tabElement: any, index: number) => (
                  <Tab
                    key={tabElement}
                    label={tabElement}
                    icon={
                      tabElement === "Trends" ? (
                        <TrendingUp sx={{ mr: 1 }} />
                      ) : (
                        <TrendingUp />
                      )
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                    classes={{
                      root: classes.trendsTabRoot,
                    }}
                  />
                ))}
              </Tabs>
            </>
          }
          headerLabel={
            activeModule === 0
              ? "Trends"
              : activeModule === 1
              ? "Cylinder Specific Indicator"
              : "Peak Pressure"
          }
          headerIcon={<></>}
          content={
            activeModule === 0 ? (
              <Trends trends={trendsData?.trends || []} fullScreen={false} />
            ) : activeModule === 1 ? (
              <CylinderIndicator
                cylinderSpecificIndicators={cylinderIndicatorData}
              />
            ) : (
              <PeakPressure
                trends={trendsData?.trends || []}
                fullScreen={false}
              />
            )
          }
          section="middle"
          initiallyCollapsed={false}
          fullScreenContent={
            activeModule === 0 ? (
              <Trends trends={trendsData?.trends || []} fullScreen={true} />
            ) : activeModule === 1 ? (
              <CylinderIndicator
                cylinderSpecificIndicators={cylinderIndicatorData}
                fullScreen={true}
              />
            ) : (
              <PeakPressure
                trends={trendsData?.trends || []}
                fullScreen={false}
              />
            )
          }
        />
      </Grid>
      <Grid item lg={4} md={12} sm={12}>
        <CardWidget
          headerLabel="Live Diagnostic Status"
          headerIcon={<VisibilityOutlined />}
          content={
            <LiveStatus
              liveStatus={liveStatus}
              processName={processName}
              currentMode={currentMode}
              setCurrentMode={setCurrentMode}
              setLiveStatus={setLiveStatus}
            />
          }
          initiallyCollapsed={true}
          setIsLiveStatusOpen={setIsLiveStatusOpen}
          fullScreenContent={
            <LiveStatus
              liveStatus={liveStatus}
              processName={processName}
              currentMode={currentMode}
              setCurrentMode={setCurrentMode}
              setLiveStatus={setLiveStatus}
            />
          }
        />
      </Grid>
      <Grid item lg={4} md={12} sm={12}>
        <CardWidget
          headerLabel={
            moduleType === "Engine"
              ? signalData?.description || ""
              : signalData?.turbineMessage || signalData?.description || ""
          }
          headerIcon={
            signalData?.resultType === "success" ? (
              <CellTowerOutlined color="success" />
            ) : (
              <SyncDisabled color="error" />
            )
          }
          content={
            <Signal
              moduleType={moduleType}
              signals={moduleData?.signals}
              formData={formData}
            />
          }
          initiallyCollapsed={true}
          fullScreenContent={
            <Signal
              moduleType={moduleType}
              signals={moduleData?.signals}
              formData={formData}
            />
          }
        />
      </Grid>
      <Grid item lg={4} md={12} sm={12}>
        <CardWidget
          headerLabel="Latest Reports"
          setIsLatestReportOpen={setIsLatestReportOpen}
          headerIcon={<DescriptionOutlined />}
          content={
            <ReportsCard
              liveStatus={liveStatus}
              processName={processName}
              formData={formData}
              moduleId={moduleId}
              setData={setData}
              setDocuments={setDocuments}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          }
          initiallyCollapsed={true}
          fullScreenContent={
            <ReportsCard
              liveStatus={liveStatus}
              processName={processName}
              formData={formData}
              moduleId={moduleId}
              setData={setData}
              setDocuments={setDocuments}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          }
        />
      </Grid>
      <div style={{ opacity: 0, height: 0, overflow: "hidden" }}>
        {documents.map((offer: any, i: number) => (
          <div
            key={`div${i}`}
            ref={(ref) => {
              elementRef.current[i] = ref;
            }}
          >
            <div id={"png" + i} style={{ minWidth: "1200px" }}>
              <DownloadPngModal open={offer} data={data} />
            </div>
          </div>
        ))}
      </div>
    </Grid>
  );
};

export default ModuleMonitoringPage;
